import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LegalDocument } from "@/components/marketing/site/legal/legal-document";
import { orpc } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";

export const Route = createFileRoute(
  "/_authenticated/_administration/settings/billing_/orders/$orderNumber",
)({
  beforeLoad: ({ context }) => guardRoute(context.me, "billing"),
  component: OrderConfirmationPage,
});

/** One Order Confirmation, as the buyer accepted it, with its printable page and PDF. */
function OrderConfirmationPage() {
  const { orderNumber } = Route.useParams();
  const order = useQuery(
    orpc.administration.billing.order.queryOptions({ input: { orderNumber } }),
  );
  const viewUrl = order.data?.viewUrl;
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`Order ${orderNumber}`}
        meta="Your Order Confirmation: the record of your financial commitment."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/settings/billing" data-testid="link-back-billing">
                Back to Billing
              </Link>
            </Button>
            {viewUrl && (
              <Button asChild size="sm">
                <a
                  href={`${viewUrl.replace("?", "/pdf?")}`}
                  target="_blank"
                  rel="noopener"
                  data-testid="link-order-pdf"
                >
                  Download PDF
                </a>
              </Button>
            )}
          </div>
        }
      />
      {order.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : order.isError ? (
        <ReadFailed
          message="Could not load this order."
          error={order.error}
          onRetry={() => void order.refetch()}
          retrying={order.isFetching}
        />
      ) : (
        <Card>
          <CardContent className="pt-6">
            <LegalDocument markdown={order.data.document} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
